// src/components/gamification/GamificationDashboard.tsx
import React from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    LinearProgress,
    Tab,
    Tabs,
    Alert,
    Snackbar,
    CircularProgress,
    Grid2 as Grid
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { gamificationService } from '../../services';
import { BadgeCard } from './BadgeCard';
import { ChallengeCard } from './ChallengeCard';
import { LeaderboardTable } from './LeaderboardTable';
import { RewardCard } from './RewardCard';
import { GamificationProfile, Badge, Challenge, LeaderboardEntry, Reward } from '../../services/interfaces';

const GamificationDashboard: React.FC = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = React.useState(0);
    const [profile, setProfile] = React.useState<GamificationProfile | null>(null);
    const [badges, setBadges] = React.useState<Badge[]>([]);
    const [challenges, setChallenges] = React.useState<Challenge[]>([]);
    const [leaderboard, setLeaderboard] = React.useState<LeaderboardEntry[]>([]);
    const [rewards, setRewards] = React.useState<Reward[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [snackbar, setSnackbar] = React.useState({ open: false, message: '' });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [profileData, badgesData, challengesData, leaderboardData, rewardsData] = await Promise.all([
                gamificationService.getProfile(""),
                gamificationService.getBadges(""),
                gamificationService.getActiveChallenges(""),
                gamificationService.getLeaderboard(""),
                gamificationService.getAvailableRewards("")
            ]);

            setProfile(profileData);
            setBadges(badgesData);
            setChallenges(challengesData);
            setLeaderboard(leaderboardData);
            setRewards(rewardsData);
        } catch (error) {
            console.error('Failed to fetch gamification data:', error);
            setError('Failed to load gamification data. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetchData();
    }, []);

    const handleClaimReward = async (rewardId: number) => {
        try {
            const success = await gamificationService.claimReward(rewardId, "");
            if (success) {
                setSnackbar({ open: true, message: t('rewardClaimSuccess') });
                fetchData();
            }
        } catch (error) {
            console.error('Failed to claim reward:', error);
            setSnackbar({ open: true, message: t('rewardClaimError') });
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box sx={{ width: '100%', mb: 4 }}>
            {profile && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="h4" gutterBottom>
                                Ниво {profile.level}
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {profile.pointsToNextLevel} поена до сљедећег нивоа
                                </Typography>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={((profile.totalPoints % 1000) / 1000) * 100}
                                    sx={{ height: 8, borderRadius: 4, mt: 1 }}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="h5">
                                    {profile.totalPoints} поена
                                </Typography>
                                <Typography variant="body1">
                                    Тренутни низ: {profile.currentStreak} дана
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Најбољи низ: {profile.longestStreak} дана
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                    <Tab label={t('gamification.challenges')} />
                    <Tab label={t('gamification.badges')} />
                    <Tab label={t('gamification.leaderboard')} />
                    <Tab label={t('gamification.rewards')} />
                </Tabs>
            </Box>

            {activeTab === 0 && (
                <Box>
                    <Typography variant="h5" gutterBottom>
                        {t('gamification.activeChallenges')}
                    </Typography>
                    <Grid container spacing={3}>
                        {challenges.map(challenge => (
                            <Grid size={{ xs: 12, sm: 6 }} key={challenge.id}>
                                <ChallengeCard challenge={challenge} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {activeTab === 1 && (
                <Box>
                    <Typography variant="h5" gutterBottom>
                        {t('gamification.yourBadges')}
                    </Typography>
                    <Grid container spacing={2}>
                        {badges.map(badge => (
                            <Grid size={{ xs: 12, sm: 6 }} key={badge.id}>
                                <BadgeCard badge={badge} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {activeTab === 2 && (
                <Box>
                    <Typography variant="h5" gutterBottom>
                        {t('gamification.leaderboard')}
                    </Typography>
                    <LeaderboardTable entries={leaderboard} />
                </Box>
            )}

            {activeTab === 3 && (
                <Box>
                    <Typography variant="h5" gutterBottom>
                        {t('gamification.availableRewards')}
                    </Typography>
                    <Grid container spacing={3}>
                        {rewards.map(reward => (
                            <Grid key={reward.id}>
                                <RewardCard 
                                    reward={reward} 
                                    onClaim={handleClaimReward}
                                    userPoints={profile?.totalPoints ?? 0}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
            />
        </Box>
    );
};

export default GamificationDashboard;